/**
 * FR-21 — Global Search + palet menu (Ctrl/⌘ K)
 *
 * Dipasang di Topbar untuk SEMUA peran:
 * - Menu sidebar peran itu dicari secara lokal, langsung saat mengetik (≥ 1 huruf)
 * - Kotak kosong yang difokuskan menampilkan palet: Favorit lalu seluruh Menu
 * - Khusus admin: pencarian server (debounce 300 ms, ≥ 2 huruf) untuk
 *   Programs · Courses · Sessions · Users, ditampilkan di bawah hasil menu
 *   (endpoint /search hanya untuk SUPER_ADMIN/PROGRAM_ADMIN)
 * - Keyboard: ↑ ↓ Enter Escape; ⌘K / Ctrl+K memfokuskan kotak dari mana saja
 * - Memilih hasil program/kursus/sesi mengganti konteks program bila perlu
 */
import React from 'react'
import { useApp } from '../context/AppContext'
import { Icon, useT } from '../shared/index'
import { globalSearch } from '../api/search.api'
import { tierLabel } from '../lib/tier'
import { getNav, navEntries } from '../components/sidebar/nav'
import { useFavorites } from '../components/sidebar/useFavorites'

// Huruf kecil tanpa diakritik, supaya "analitik" mengenai "Analitik" dan
// "pengaturan" mengenai "Pengaturan" apa pun kapitalisasinya.
const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

const IS_MAC = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || '')

export function GlobalSearch() {
  const { lang, role, navigate, setCurrentProgramId, user } = useApp()
  const t = useT()
  const canSearchApi = role === 'admin'

  const [query, setQuery] = React.useState('')
  const [results, setResults] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [cursor, setCursor] = React.useState(0)

  const wrapRef = React.useRef(null)
  const inputRef = React.useRef(null)
  const dropdownRef = React.useRef(null)

  const trimmed = query.trim()

  // ── Menu sidebar yang cocok (lokal, tanpa jaringan) ─────────
  const nav = React.useMemo(() => getNav(role, t, lang, { superAdmin: user?.role === 'SUPER_ADMIN' }), [role, t, lang, user?.role])
  const entries = React.useMemo(() => navEntries(nav), [nav])
  const { favs } = useFavorites(role)

  const { favItems, menuItems } = React.useMemo(() => {
    const q = norm(trimmed)
    if (!q) {
      // Palet: favorit di atas, sisanya di bawah (tanpa menampilkan favorit dua kali).
      const fav = favs.map((id) => entries.find((e) => e.id === id)).filter(Boolean)
      const favIds = new Set(fav.map((e) => e.id))
      return { favItems: fav, menuItems: entries.filter((e) => !favIds.has(e.id)) }
    }
    const tokens = q.split(/\s+/)
    const hit = entries.filter((e) => {
      const hay = norm(`${e.label} ${e.group || ''} ${e.id}`)
      return tokens.every((tk) => hay.includes(tk))
    })
    // Yang labelnya diawali kata kunci naik ke atas (sort stabil: urutan menu terjaga).
    hit.sort((a, b) => Number(norm(b.label).startsWith(q)) - Number(norm(a.label).startsWith(q)))
    return { favItems: [], menuItems: hit }
  }, [entries, favs, trimmed])

  // ── Pencarian server, debounce (admin saja) ─────────────────
  React.useEffect(() => {
    if (!canSearchApi || trimmed.length < 2) {
      setResults(null)
      setLoading(false)
      return undefined
    }
    let batal = false
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const data = await globalSearch(trimmed)
        if (!batal) setResults(data)
      } catch {
        // silently ignore (e.g. 403 for non-admin)
      } finally {
        if (!batal) setLoading(false)
      }
    }, 300)
    return () => { batal = true; clearTimeout(timer) }
  }, [trimmed, canSearchApi])

  // ── Flatten all results for keyboard cursor ─────────────────
  // Urutannya harus sama dengan urutan render di bawah: Favorit, Menu, lalu
  // grup hasil server.
  const allItems = React.useMemo(() => {
    const api = results
      ? [
          ...(results.programs || []).map((p) => ({ type: 'program', data: p })),
          ...(results.courses || []).map((c) => ({ type: 'course', data: c })),
          ...(results.sessions || []).map((s) => ({ type: 'session', data: s })),
          ...(results.users || []).map((u) => ({ type: 'user', data: u })),
        ]
      : []
    return [
      ...favItems.map((d) => ({ type: 'menu', data: d, fav: true })),
      ...menuItems.map((d) => ({ type: 'menu', data: d })),
      ...api,
    ]
  }, [favItems, menuItems, results])

  // Kursor kembali ke butir pertama tiap kata kunci berubah; di-clamp bila
  // daftar menyusut (mis. hasil server datang lalu hilang).
  React.useEffect(() => { setCursor(0) }, [trimmed])
  const activeIdx = allItems.length ? Math.min(cursor, allItems.length - 1) : -1

  // Butir yang disorot lewat keyboard harus tetap terlihat di dropdown yang bergulir.
  React.useEffect(() => {
    if (!open) return
    dropdownRef.current?.querySelector('.gs-item.active')?.scrollIntoView({ block: 'nearest' })
  }, [activeIdx, open])

  // ── Close on outside click ──────────────────────────────────
  React.useEffect(() => {
    function onMouseDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  // ── ⌘K / Ctrl+K global shortcut ────────────────────────────
  React.useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
        setOpen(true)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // ── Navigation on select ────────────────────────────────────
  function handleSelect(item) {
    setOpen(false)
    setQuery('')
    setResults(null)
    inputRef.current?.blur()

    switch (item.type) {
      case 'menu':
        navigate(item.data.id)
        break
      case 'program':
        setCurrentProgramId(item.data.id)
        navigate('program-detail')
        break
      case 'course':
        setCurrentProgramId(item.data.program_id)
        navigate('course', { courseId: item.data.id })
        break
      case 'session':
        setCurrentProgramId(item.data.program_id)
        navigate('training')
        break
      case 'user':
        navigate('users')
        break
      default:
        break
    }
  }

  // ── Keyboard handler ────────────────────────────────────────
  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      // Esc pertama menutup daftar; yang kedua melepas fokus dari kotak.
      if (open) setOpen(false)
      else inputRef.current?.blur()
      return
    }
    if (allItems.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setCursor(Math.min(activeIdx + 1, allItems.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setOpen(true)
      setCursor(Math.max(activeIdx - 1, 0))
    } else if (e.key === 'Enter' && open && activeIdx >= 0) {
      e.preventDefault()
      handleSelect(allItems[activeIdx])
    }
  }

  // ── Computed state ──────────────────────────────────────────
  const apiEmpty =
    !results ||
    (!results.programs?.length &&
      !results.courses?.length &&
      !results.sessions?.length &&
      !results.users?.length)
  const noResults = trimmed.length > 0 && !loading && allItems.length === 0

  const placeholder = canSearchApi ? t.gsPlaceholderAdmin : t.gsPlaceholderMenu

  // Shared counter for keyboard cursor index (reset per render)
  let itemIdx = 0

  function renderItem(type, data, key) {
    const idx = itemIdx++
    const active = activeIdx === idx

    let icon, name, meta

    switch (type) {
      case 'menu':
        icon = <Icon name={data.icon} size={13} color="var(--text-2)" />
        name = data.label
        meta = data.group ? data.group.charAt(0) + data.group.slice(1).toLowerCase() : t.gsMenu
        break
      case 'program':
        icon = <Icon name="anchor" size={13} color="#fff" />
        name = data.name
        meta = [tierLabel(data), data.code].filter(Boolean).join(' · ')
        break
      case 'course':
        icon = <Icon name="book" size={13} color="var(--text-2)" />
        name = data.title
        meta = data.program?.name || ''
        break
      case 'session': {
        const d = new Date(data.scheduled_at)
        const dateStr = d.toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US', {
          day: 'numeric', month: 'short', year: 'numeric',
        })
        icon = <Icon name="calendar" size={13} color="var(--text-2)" />
        name = data.title
        meta = [dateStr, data.location, data.program?.name].filter(Boolean).join(' · ')
        break
      }
      case 'user': {
        const roleLabel = {
          SUPER_ADMIN: 'Super Admin', PROGRAM_ADMIN: 'Admin',
          TRAINER: 'Trainer', PARTICIPANT: 'Peserta',
        }[data.role] || data.role
        icon = <Icon name="user" size={13} color="var(--text-2)" />
        name = data.full_name
        meta = `${data.email} · ${roleLabel}`
        break
      }
      default:
        return null
    }

    const iconBg = type === 'program'
      ? (data.color_theme || 'var(--accent)')
      : 'var(--bg-2)'

    return (
      <div
        key={key}
        className={`gs-item${active ? ' active' : ''}`}
        onMouseEnter={() => setCursor(idx)}
        onMouseDown={(e) => { e.preventDefault(); handleSelect({ type, data }) }}
      >
        <div className="gs-item-icon" style={{ background: iconBg }}>
          {icon}
        </div>
        <div className="gs-item-body">
          <div className="gs-item-name">{name}</div>
          <div className="gs-item-meta">{meta}</div>
        </div>
        {type === 'course' && (
          <span className={`gs-status ${data.status === 'PUBLISHED' ? 'published' : 'draft'}`}>
            {data.status === 'PUBLISHED' ? (lang === 'id' ? 'Publik' : 'Live') : 'Draft'}
          </span>
        )}
      </div>
    )
  }

  // ── Render ──────────────────────────────────────────────────
  return (
    <div className="gs-wrap" ref={wrapRef}>
      <Icon name="search" size={14} color="var(--text-3)" />
      <input
        ref={inputRef}
        className="search-input"
        placeholder={placeholder}
        aria-label={placeholder}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        spellCheck={false}
      />
      <span className="kbd" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}>
        {IS_MAC ? '⌘K' : 'Ctrl K'}
      </span>

      {open && (
        <div className="gs-dropdown" ref={dropdownRef}>
          {favItems.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-label">{t.navFavorites}</div>
              {favItems.map((e) => renderItem('menu', e, `fav-${e.id}`))}
            </div>
          )}
          {menuItems.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-label">{t.gsMenu}</div>
              {menuItems.map((e) => renderItem('menu', e, `menu-${e.id}`))}
            </div>
          )}

          {canSearchApi && loading && (
            <div className="gs-empty">
              <Icon name="refresh" size={14} color="var(--text-4)" />
              <span>{lang === 'id' ? 'Mencari…' : 'Searching…'}</span>
            </div>
          )}

          {noResults && (
            <div className="gs-empty">
              <Icon name="search" size={14} color="var(--text-4)" />
              <span>
                {lang === 'id' ? `Tidak ada hasil untuk "${trimmed}"` : `No results for "${trimmed}"`}
              </span>
            </div>
          )}

          {canSearchApi && !loading && !apiEmpty && (
            <>
              {results.programs?.length > 0 && (
                <div className="gs-group">
                  <div className="gs-group-label">{lang === 'id' ? 'Program' : 'Programs'}</div>
                  {results.programs.map((p) => renderItem('program', p, `prog-${p.id}`))}
                </div>
              )}
              {results.courses?.length > 0 && (
                <div className="gs-group">
                  <div className="gs-group-label">{lang === 'id' ? 'Kursus' : 'Courses'}</div>
                  {results.courses.map((c) => renderItem('course', c, `course-${c.id}`))}
                </div>
              )}
              {results.sessions?.length > 0 && (
                <div className="gs-group">
                  <div className="gs-group-label">{lang === 'id' ? 'Sesi' : 'Sessions'}</div>
                  {results.sessions.map((s) => renderItem('session', s, `sess-${s.id}`))}
                </div>
              )}
              {results.users?.length > 0 && (
                <div className="gs-group">
                  <div className="gs-group-label">{lang === 'id' ? 'Pengguna' : 'Users'}</div>
                  {results.users.map((u) => renderItem('user', u, `user-${u.id}`))}
                </div>
              )}
            </>
          )}

          <div className="gs-footer">
            <span className="gs-hint">↑↓ navigasi</span>
            <span className="gs-hint">↵ pilih</span>
            <span className="gs-hint">Esc tutup</span>
          </div>
        </div>
      )}
    </div>
  )
}
